import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MdClose, MdFilterList } from 'react-icons/md';
import type { GeneratedIdea } from '../../redux/slices/generatedIdeasSlice';

interface AllGeneratedIdeasModalProps {
  isOpen: boolean;
  onClose: () => void;
  ideas: GeneratedIdea[];
  selectedFilter: string;
  onFilterChange: (filter: string) => void;
}

const AllGeneratedIdeasModal: React.FC<AllGeneratedIdeasModalProps> = ({
  isOpen,
  onClose,
  ideas,
  selectedFilter,
  onFilterChange,
}) => {
  const [isFilterDropdownOpen, setIsFilterDropdownOpen] = useState(false);
  
  // Date filter options (All first, then Last 2 Weeks)
  const dateFilters = ['All', 'Last 2 Weeks'];

  // Filter ideas based on selected date filter
  const filteredIdeas = React.useMemo(() => {
    if (selectedFilter === 'Last 2 Weeks') {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const twoWeeksAgo = new Date(today);
      twoWeeksAgo.setDate(today.getDate() - 14);

      return ideas.filter(idea => {
        const eventDate = new Date(idea.trigger_event_date);
        eventDate.setHours(0, 0, 0, 0);
        return eventDate >= twoWeeksAgo;
      });
    }
    
    // 'All' - return all ideas
    return ideas;
  }, [ideas, selectedFilter]);
  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/70 z-50"
          />

          {/* Modal */}
          <div className="fixed inset-0 z-50 flex items-center justify-center p-6">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.2 }}
              className="w-full max-w-[1400px] max-h-[90vh] flex flex-col rounded-2xl overflow-hidden bg-[#1a1a1a] border border-gray-700"
              style={{
                backdropFilter: 'blur(10px)',
                boxShadow: '0 0 30px rgba(0,0,0,0.5)',
              }}
            >
              {/* Header */}
              <div className="px-6 py-4 border-b border-gray-700">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <h2 className="text-2xl font-bold text-[#EE4423]">
                      All AI Generated Campaign Ideas ({filteredIdeas.length})
                    </h2>
                    
                    {/* Filter Icon with Dropdown */}
                    <div className="relative">
                      <button
                        onClick={() => setIsFilterDropdownOpen(!isFilterDropdownOpen)}
                        className="p-2 rounded-lg bg-gray-700/50 hover:bg-gray-600/50 text-gray-400 hover:text-white transition-colors border border-gray-600/50"
                        title="Filter"
                      >
                        <MdFilterList size={20} />
                      </button>

                      {/* Dropdown */}
                      <AnimatePresence>
                        {isFilterDropdownOpen && (
                          <>
                            {/* Backdrop */}
                            <div
                              className="fixed inset-0 z-10"
                              onClick={() => setIsFilterDropdownOpen(false)}
                            />
                            
                            {/* Dropdown Content */}
                            <motion.div
                              initial={{ opacity: 0, y: -10 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: -10 }}
                              transition={{ duration: 0.2 }}
                              className="absolute top-full left-0 mt-2 bg-gray-700 border border-gray-600 rounded-lg shadow-xl p-3 min-w-[180px] z-20"
                            >
                              <div className="space-y-2">
                                {dateFilters.map((filter) => (
                                  <label
                                    key={filter}
                                    className="flex items-center gap-3 cursor-pointer hover:bg-gray-600/50 p-2 rounded-lg transition-colors"
                                  >
                                    <input
                                      type="radio"
                                      name="dateFilterModal"
                                      value={filter}
                                      checked={selectedFilter === filter}
                                      onChange={(e) => {
                                        onFilterChange(e.target.value);
                                        setIsFilterDropdownOpen(false);
                                      }}
                                      className="w-4 h-4 text-[#EE4423] bg-gray-600 border-gray-500 focus:ring-[#EE4423] focus:ring-2"
                                    />
                                    <span className="text-sm text-white">{filter}</span>
                                  </label>
                                ))}
                              </div>
                            </motion.div>
                          </>
                        )}
                      </AnimatePresence>
                    </div>
                  </div>
                  
                  <button
                    onClick={onClose}
                    className="p-2 hover:bg-gray-700/50 cursor-pointer rounded-full transition-colors"
                  >
                    <MdClose size={24} className="text-white" />
                  </button>
                </div>
              </div>

              {/* Content - Scrollable Grid */}
              <div className="flex-1 overflow-y-auto p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {filteredIdeas.map((idea) => {
                    // Parse color_theme if it's a string
                    const colorTheme =
                      typeof idea.color_theme === 'string'
                        ? JSON.parse(idea.color_theme)
                        : idea.color_theme;

                    return (
                      <motion.div
                        key={idea.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3 }}
                        className="rounded-2xl overflow-hidden bg-[#212121] border border-gray-700/50 hover:border-[#EE4423]/50 transition-all"
                        style={{
                          backdropFilter: 'blur(10px)',
                          boxShadow: '0 0 14px 0 #0000001a',
                        }}
                      >
                        {/* Card Header with Color Theme */}
                        <div className="h-2"
                        />

                        {/* Card Content */}
                        <div className="p-3">
                          {/* Event Badge */}
                          {/* <div className="mb-3">
                            <span className="px-2 py-1 bg-[#EE4423]/10 border border-[#EE4423]/50 text-[#fff] text-[10px] rounded-full font-normal">
                              {idea.trigger_event_name}
                            </span>
                          </div> */}

                          {/* Title */}
                          <h3 className="text-white font-semibold text-sm mb-4 line-clamp-2 min-h-[40px]">
                            {idea.idea_headline}
                          </h3>

                          {/* Date and Type */}
                          <div className="flex items-center justify-between mb-3">
                            <span className="text-gray-400 text-xs">
                              {new Date(idea.trigger_event_date).toLocaleDateString(
                                'en-US',
                                {
                                  month: 'short',
                                  day: 'numeric',
                                  year: 'numeric',
                                }
                              )}
                            </span>
                            <span className="text-gray-400 text-xs italic">
                              {idea.idea_type}
                            </span>
                          </div>

                          {/* Description */}
                          <p className="text-gray-400 text-xs leading-relaxed mb-4 line-clamp-3">
                            {idea.quick_win_details}
                          </p>

                          {/* Action Button */}
                          <button className="w-full py-2 text-right cursor-pointer text-[#ee4423] text-xs font-medium rounded-lg transition-colors

">
                            View Idea
                          </button>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};

export default AllGeneratedIdeasModal;
